import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx } from "../../apiClient";
import { Card, Badge, SectionTitle, RefreshBtn, Btn } from "../primitives";

export const AiRoutingAdmin = () => {
  const [providers, setProviders] = useState<any[]>([]);
  const [tiers, setTiers] = useState<any[]>([]);
  const [health, setHealth] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [tierMeta, setTierMeta] = useState<any>({});
  // Per-tenant BYO AI key: write-only status + capture. Routing config unlocks when set.
  const [keyStatus, setKeyStatus] = useState<{ configured: boolean; routing_unlocked: boolean }>({ configured: false, routing_unlocked: false });
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [keyMsg, setKeyMsg] = useState(""); const [keyErr, setKeyErr] = useState("");

  const loadKey = async () => { const r = await apiCall('/settings/ai-key'); if (r) setKeyStatus({ configured: !!r.configured, routing_unlocked: !!r.routing_unlocked }); };
  const saveKey = async () => {
    if (!keyInput.trim()) return;
    setSavingKey(true); setKeyErr(""); setKeyMsg("");
    const r = await apiCallEx('/settings/ai-key', { method: 'PUT', body: JSON.stringify({ provider: 'anthropic', api_key: keyInput }) });
    setSavingKey(false);
    if (r.ok && r.data?.success) { setKeyInput(""); setKeyMsg("AI key validated and saved. Your AI now runs on your account."); loadKey(); }
    else { setKeyErr(r.data?.message || (r.data?.error === 'invalid_key' ? "That AI key could not be validated with the provider and was not saved." : "Could not save the key.")); }
  };
  const clearKey = async () => {
    if (!window.confirm("Remove your AI key? AI will fall back to the KontractAI default.")) return;
    setSavingKey(true); setKeyErr(""); setKeyMsg("");
    const r = await apiCallEx('/settings/ai-key', { method: 'DELETE' });
    setSavingKey(false);
    if (r.ok) { setKeyMsg("AI key removed — running on the KontractAI default."); loadKey(); } else { setKeyErr("Could not remove the key."); }
  };

  const loadAll = async () => {
    setLoading(true);
    const [pRes, tRes, hRes, mRes] = await Promise.all([apiCall('/ai-config/providers'), apiCall('/ai-config/tiers'), apiCall('/ai-config/health'), apiCall('/ai-config/tier-metadata')]);
    setProviders(pRes?.providers || []);
    setTiers(tRes?.tiers || []);
    setHealth(hRes?.health || []);
    setTierMeta(mRes?.metadata || {});
    setLoading(false);
  };
  useEffect(() => { loadAll(); loadKey(); }, []);

  const toggleZdr = async (p: any) => { setSaving(`p-${p.id}`); await apiCall(`/ai-config/providers/${p.id}`, { method: 'PUT', body: JSON.stringify({ zdr_enabled: !p.zdr_enabled }) }); await loadAll(); setSaving(null); };

  const updateTier = async (tier: number, role: string, providerId: number, modelId: string) => {
    setSaving(`t-${tier}-${role}`);
    await apiCall(`/ai-config/tiers/${tier}/${role}`, { method: 'PUT', body: JSON.stringify({ provider_id: providerId, model_id: modelId }) });
    await loadAll();
    setSaving(null);
  };

  const tierDesc = (t: number) => tierMeta[t]?.description || (t === 1 ? 'Frontier quality' : t === 2 ? 'General-purpose' : 'Mechanical tasks');

  if (loading) return <div style={{ padding: 20, color: T.txM }}>Loading AI routing configuration...</div>;

  return (
    <div>
      <SectionTitle sub="Configure which AI provider handles each tier">AI Routing</SectionTitle>

      {/* Bring Your Own AI key (per-tenant, encrypted, validated on save). Unlocks routing config. */}
      <Card style={{ marginBottom: 16, border: `1px solid ${keyStatus.configured ? T.grn : T.brd}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 6 }}>Your AI key (Anthropic)</div>
        <div style={{ fontSize: 12, color: T.txM, marginBottom: 10 }}>
          Bring your own AI provider key so your AI runs on your account. The key is validated on save,
          encrypted at rest, never shown again, and never used by another customer. Without one you run on
          the KontractAI default.
        </div>
        {keyErr && <div style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 4, backgroundColor: T.redS, color: T.red, fontSize: 12 }}>{keyErr}</div>}
        {keyMsg && <div style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 4, backgroundColor: T.grnS, color: T.grn, fontSize: 12 }}>{keyMsg}</div>}
        {keyStatus.configured ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Badge c={T.grn} b={T.grnS}>Configured</Badge>
            <span style={{ fontSize: 12, color: T.txM }}>Your key is set — routing config is unlocked.</span>
            <Btn v="ghost" sz="sm" disabled={savingKey} onClick={clearKey}>Remove key</Btn>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input type="password" autoComplete="off" value={keyInput} onChange={e => setKeyInput(e.target.value)} placeholder="sk-ant-…" style={{ flex: 1, minWidth: 240, padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace" }} />
            <Btn v="primary" sz="sm" disabled={savingKey || !keyInput.trim()} onClick={saveKey}>{savingKey ? "Validating…" : "Save & validate"}</Btn>
          </div>
        )}
      </Card>

      {!keyStatus.routing_unlocked && (
        <Card style={{ marginBottom: 16, backgroundColor: T.bg3, border: `1px dashed ${T.brd}` }}>
          <div style={{ fontSize: 12.5, color: T.txM }}>🔒 Add your own AI key above to manage AI routing for your account. On the KontractAI default, routing is managed by KontractAI.</div>
        </Card>
      )}

      <div style={{ opacity: keyStatus.routing_unlocked ? 1 : 0.5, pointerEvents: keyStatus.routing_unlocked ? "auto" : "none" }}>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Provider Status</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ backgroundColor: T.bg3 }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${T.brd}` }}>Provider</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', borderBottom: `1px solid ${T.brd}` }}>Type</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', borderBottom: `1px solid ${T.brd}` }}>ZDR</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', borderBottom: `1px solid ${T.brd}` }}>Active</th>
            </tr></thead>
            <tbody>{providers.map((p: any) => (
              <tr key={p.id}>
                <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.brdSubtle}`, fontWeight: 600 }}>{p.display_name}</td>
                <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.brdSubtle}`, color: T.txM }}>{p.provider_type}</td>
                <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.brdSubtle}`, textAlign: 'center' }}>
                  {p.zdr_required ? <button onClick={() => toggleZdr(p)} disabled={saving === `p-${p.id}`} style={{ padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#fff', backgroundColor: p.zdr_enabled ? T.grn : T.red }}>{p.zdr_enabled ? 'ON' : 'OFF'}</button> : <span style={{ color: T.txD, fontSize: 10 }}>N/A</span>}
                </td>
                <td style={{ padding: '8px 10px', borderBottom: `1px solid ${T.brdSubtle}`, textAlign: 'center' }}><Badge c={p.is_active ? T.grn : T.txD} b={p.is_active ? T.grnS : T.bg3}>{p.is_active ? 'Active' : 'Inactive'}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div style={{ fontSize: 10, color: T.txD, marginTop: 8 }}>ZDR must be enabled with the provider externally before toggling here. Self-hosted Ollama does not require ZDR.</div>
      </Card>

      {[1, 2, 3].map(tierNum => {
        const roles = ['primary', 'fallback', 'tertiary'];
        return (
          <Card key={tierNum} style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.tx, marginBottom: 4 }}>{tierMeta[tierNum]?.name || `Tier ${tierNum}`}</div>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 6 }}>{tierDesc(tierNum)}</div>
              {tierMeta[tierNum]?.surfaces && (
                <details style={{ marginBottom: 8 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 11, fontWeight: 600, color: T.acc, padding: '4px 0' }}>
                    {tierMeta[tierNum].surfaces.length} AI surfaces use this tier
                  </summary>
                  <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                    {tierMeta[tierNum].surfaces.map((s: any) => (
                      <div key={s.key} style={{ padding: '6px 10px', backgroundColor: T.bg3, borderRadius: 4, fontSize: 11 }}>
                        <strong style={{ color: T.tx }}>{s.name}</strong>
                        <span style={{ color: T.txD, marginLeft: 6 }}>{s.description}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ backgroundColor: T.bg3 }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${T.brd}`, width: 100 }}>Role</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Provider</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Model</th>
              </tr></thead>
              <tbody>{roles.map(role => {
                // Data filter, not permission gate.
                const cfg = tiers.find((t: any) => t.tier === tierNum && t.role === role);
                return (
                  <tr key={role}>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}`, textTransform: 'capitalize', fontWeight: 500 }}>{role}</td>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}` }}>
                      <select value={cfg?.provider_id || ''} onChange={e => { const pid = parseInt(e.target.value, 10); const prov = providers.find((p: any) => p.id === pid); const fm = prov?.available_models?.[0]?.id; if (fm) updateTier(tierNum, role, pid, fm); }} disabled={saving === `t-${tierNum}-${role}`} style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, fontFamily: 'inherit' }}>
                        <option value="">-- None --</option>
                        {providers.filter((p: any) => p.is_active && (!p.zdr_required || p.zdr_enabled)).map((p: any) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}` }}>
                      <select value={cfg?.model_id || ''} onChange={e => { if (cfg) updateTier(tierNum, role, cfg.provider_id, e.target.value); }} disabled={!cfg || saving === `t-${tierNum}-${role}`} style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 11, fontFamily: 'inherit' }}>
                        {cfg && (() => { const prov = providers.find((p: any) => p.id === cfg.provider_id); return (prov?.available_models || []).map((m: any) => <option key={m.id} value={m.id}>{m.display_name}</option>); })()}
                      </select>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </Card>
        );
      })}
      </div>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Provider Health (Last Hour)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ backgroundColor: T.bg3 }}>
            <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Provider</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Calls</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Errors</th>
            <th style={{ textAlign: 'right', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Avg Latency</th>
            <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: `1px solid ${T.brd}` }}>Last Call</th>
          </tr></thead>
          <tbody>{health.map((h: any) => {
            const errRate = h.calls_last_hour > 0 ? (h.errors_last_hour / h.calls_last_hour * 100) : 0;
            return (
              <tr key={h.id}>
                <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}`, fontWeight: 500 }}>{h.display_name}</td>
                <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}`, textAlign: 'right' }}>{h.calls_last_hour}</td>
                <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}`, textAlign: 'right', color: errRate > 5 ? T.red : T.tx }}>{h.errors_last_hour} ({errRate.toFixed(0)}%)</td>
                <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}`, textAlign: 'right' }}>{h.avg_latency_ms != null ? `${h.avg_latency_ms}ms` : '-'}</td>
                <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.brdSubtle}`, color: T.txM }}>{h.last_call_at ? new Date(h.last_call_at).toLocaleString() : 'never'}</td>
              </tr>
            );
          })}</tbody>
        </table>
        <div style={{ marginTop: 8 }}><RefreshBtn onClick={() => loadAll()} /></div>
      </Card>
    </div>
  );
};
